import { AsyncPipe, DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Observable, fromEvent, map, startWith } from 'rxjs';

const SCROLL_THRESHOLD_PX = 100;
const MAIN_SCROLL_ID = 'app-main-scroll';

@Component({
  selector: 'app-scroll-top',
  imports: [AsyncPipe, MatButtonModule, MatIconModule, MatTooltipModule],
  templateUrl: './scroll-top.component.html',
  styleUrl: './scroll-top.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScrollTopComponent {
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);

  /** Capture phase so inner scroll hosts (dashboard) are detected, not only window. */
  readonly isVisible$: Observable<boolean> = fromEvent(this.document, 'scroll', {
    capture: true,
    passive: true,
  }).pipe(
    startWith(null),
    map(() => this.isPastThreshold()),
    takeUntilDestroyed(this.destroyRef),
  );

  scrollToTop(): void {
    const mainScrollEl = this.getMainScrollEl();

    if (mainScrollEl && mainScrollEl.scrollTop > 0) {
      mainScrollEl.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    this.document.body.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
      inline: 'start',
    });
  }

  private getMainScrollEl(): HTMLElement | null {
    return this.document.getElementById(MAIN_SCROLL_ID);
  }

  private isPastThreshold(): boolean {
    const mainScrollEl = this.getMainScrollEl();
    const windowPast =
      window.scrollY > SCROLL_THRESHOLD_PX ||
      this.document.documentElement.scrollTop > SCROLL_THRESHOLD_PX ||
      this.document.body.scrollTop > SCROLL_THRESHOLD_PX;
    const mainPast = !!mainScrollEl && mainScrollEl.scrollTop > SCROLL_THRESHOLD_PX;

    return windowPast || mainPast;
  }
}
